// Schedule.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import "../App.css";

const API_BASE = "https://bunny-klawz.uc.r.appspot.com";
const daysOfWeek = ["", "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Helper: Get start of the week (Sunday) in local time
const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0); // midnight local time
    return d;
};

// Helper: Format to YYYY-MM-DD in local time (no UTC shift)
const formatToLocalDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatTime = (timeStr) => {
    const date = new Date(timeStr);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
        // weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        // timeZoneName: 'short', // or 'long' for full time zone name
    });
};

const formatDateTime = (date) => {
    return date.toLocaleDateString("en-US", {
        // weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: 'short', // or 'long' for full time zone name
    });
};

const generateTimeline = () => {
    const times = [];
    const totalSlots = 24;

    for (let i = 0; i < totalSlots; i++) {
        const hour = Math.floor(i);
        const minutes = i % 1 === 0 ? '00' : '30';
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;
        const ampm = hour < 12 ? 'AM' : 'PM';

        times.push(`${hour12}:${minutes} ${ampm}`)
    }

    return times;
}

const Schedule = ({ appointments, setAppointments, filteredAppointments, setFilteredAppointments }) => {

    const [workingHours, setWorkingHours] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(() => {
        const startOfWeek = getStartOfWeek(new Date());
        return formatToLocalDateKey(startOfWeek);
    });

    const [weekDates, setWeekDates] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [activeDate, setActiveDate] = useState(getStartOfWeek(new Date()));
    const [isMobile, setIsMobile] = useState(window.innerWidth < 666);

    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState(null);

    const [cancelling, setCancelling] = useState(false);
    const [rescheduling, setRescheduling] = useState(false);

    const [selectedSlot, setSelectedSlot] = useState(null);



    const handleClick = (data) => {
        setModalContent(data);
        setShowModal(true);
    };

    const cancelAppointment = async () => {
        const reasonInput = document.querySelector("textarea[name='cancellationReason']");
        const reason = reasonInput ? reasonInput.value.trim() : "";

        if (!modalContent || !modalContent._id) {
            console.error("Invalid appointment data.");
            return;
        }

        const cancellationTime = new Date().toISOString();
        const updatedDetails = [...modalContent.appointmentDetails];
        updatedDetails[6] = { cancellationReason: reason, cancellationTime };

        try {
            const res = await axios.put(`${API_BASE}/appointments/${modalContent._id}`, {
                ...modalContent,
                status: "cancelled",
                appointmentDetails: updatedDetails
            });

            if (res.status === 200) {
                const updated = res.data;

                await fetch(`${API_BASE}/schedule-email`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        emailId: updated._id,
                        toEmail: updated.user.email,
                        toName: updated.user.name,
                        subject: "Your appointment has been cancelled.",
                        message: `Your appointment for ${updated.appointmentDetails[0]} on ${updated.localDateTime} has been cancelled. If this was a mistake or you’d like to reschedule, you can book a new appointment at https://bunnyklawz.com.\n\nPlease do not respond back. This is an automated message. Replies will not be checked.\nPlease reach out via Instagram @bunnyklaws for any questions.`,
                    }),
                });

                const newAppointments = filteredAppointments.map(appt =>
                    appt._id === updated._id ? updated : appt
                );
                setFilteredAppointments(newAppointments);
                setAppointments(newAppointments);
            }

            closeModal();
        } catch (err) {
            console.error("Failed to cancel appointment:", err);
        }
    };


    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 666px)');

        const handleChange = (e) => {
            setIsMobile(e.matches);
        };

        mediaQuery.addEventListener('change', handleChange);

        // Set initial state in case window was resized before mount
        setIsMobile(mediaQuery.matches);

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const [apptRes, hoursRes] = await Promise.all([
                axios.get(`${API_BASE}/appointments`),
                axios.get(`${API_BASE}/working-hours`)
            ]);

            setAppointments(apptRes.data);
            setFilteredAppointments(apptRes.data);

            // const confirmedAppts = apptRes.data.filter(appt => appt.status === "confirmed");
            // setFilteredAppointments(confirmedAppts);
            setWorkingHours(hoursRes.data);

            // Generate weeks and update selectedWeek if needed
            const generatedWeeks = generateWeeks(apptRes.data);
            setWeeks(generatedWeeks);

            // If no selectedWeek exists in the working hours, use the first available week
            if (!generatedWeeks.find(w => formatToLocalDateKey(w) === selectedWeek)) {
                setSelectedWeek(generatedWeeks[0]?.toISOString().slice(0, 10) || selectedWeek);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const start = parseLocalDate(selectedWeek);
        const dates = [];

        for (let i = 0; i < 7; i++) {
            const date = new Date(start);
            date.setDate(start.getDate() + i);
            dates.push(formatDate(date));
        }

        setActiveDate(new Date());
        setWeekDates(dates);
    }, [selectedWeek]);

    const parseLocalDate = (dateStr) => {
        const [year, month, day] = dateStr.split("-").map(Number);
        return new Date(year, month - 1, day); // Note: month is 0-indexed
    };


    const formatWeekRange = (startDate) => {
        const start = new Date(startDate);
        const end = new Date(startDate);
        end.setDate(start.getDate() + 6);

        const options = { month: "short", day: "numeric" };
        const year = end.getFullYear();

        return `${start.toLocaleDateString("en-US", options)} - ${end.toLocaleDateString("en-US", options)}, ${year}`;
    };

    const generateWeeks = (data) => {
        const weekMap = new Map();

        // Get reference weeks in local time
        const now = new Date();
        const thisWeek = getStartOfWeek(now);
        const lastWeek = getStartOfWeek(new Date(thisWeek.getTime() - 7 * 24 * 60 * 60 * 1000));
        const nextWeek = getStartOfWeek(new Date(thisWeek.getTime() + 7 * 24 * 60 * 60 * 1000));

        // Add default weeks
        [lastWeek, thisWeek, nextWeek].forEach((weekStart) => {
            const key = formatToLocalDateKey(weekStart);
            weekMap.set(key, weekStart);
        });

        // Add weeks from data
        data.forEach((item) => {
            const dateStr = item.appointmentDetails?.[5]?.date;
            if (!dateStr) return;

            const startOfWeek = getStartOfWeek(new Date(dateStr));
            if (startOfWeek < lastWeek) return;

            const key = formatToLocalDateKey(startOfWeek);
            if (!weekMap.has(key)) {
                weekMap.set(key, startOfWeek);
            }
        });

        // Return sorted weeks
        return Array.from(weekMap.values()).sort((a, b) => a - b);
    };


    const getItemsForDay = (day, items) => {
        return items.filter((item) => {
            if (item.status !== "confirmed") return;
            const itemDate = new Date(item.appointmentDetails[5].date);
            const start = new Date(selectedWeek); // fresh date every time
            const currentDay = new Date(start);
            currentDay.setDate(start.getDate() + day);
            return itemDate.toDateString() === currentDay.toDateString();
        });
    };

    const getUpcomingAppointmentSlots = (appt) => {
        const tech = appt.appointmentDetails[5].artist;

        return getAvailableSlotsForTech(
            workingHours,
            filteredAppointments,
            tech,
            appt
        );
    };



    function getAvailableSlotsForTech(workingHours, appointments, techName, apptDetails) {
        const now = new Date();

        // Get all future working hours for this nail tech
        const working = workingHours.filter(hour =>
            hour.nailtech === techName && new Date(hour.end) > now
        );

        const appts = appointments.filter(appt =>
            appt.status === "confirmed" &&
            appt.appointmentDetails?.[5]?.artist === techName
        );

        const bookedSlots = appts.map(appt => {
            const start = new Date(appt.appointmentDetails[5].date);
            const end = new Date(start.getTime() + appt.appointmentDetails[5].duration * 60000);
            return { start, end };
        });

        const availableSlots = [];

        for (let wh of working) {
            let start = new Date(wh.start);
            const end = new Date(wh.end);

            // Skip past blocks that already ended
            if (end <= now) continue;

            // Adjust start time if working block already began today
            if (start < now) start = new Date(now);

            while (start.getTime() + apptDetails.appointmentDetails[5].duration * 60000 <= end.getTime()) {
                const potentialEnd = new Date(start.getTime() + apptDetails.appointmentDetails[5].duration * 60000);

                const overlaps = bookedSlots.some(({ start: bs, end: be }) =>
                    (start < be && potentialEnd > bs)
                );

                if (!overlaps) {
                    availableSlots.push({ start: new Date(start), end: potentialEnd });
                }

                // Move to the next 15-min interval
                start = new Date(start.getTime() + 15 * 60000);
            }
        }

        return availableSlots;
    }

    const truncateWithEllipses = (str) => {
        return str.length > 10 ? str.slice(0, 7) + '...' : str;
    };

    const getDuration = (start, end) => {
        return Math.floor((end - start) / 1000 / 60);
    }

    function timeStringToMinutes(timeStr) {
        const [time, period] = timeStr.split(' ');
        const [hourStr, minuteStr] = time.split(':');

        let hours = parseInt(hourStr, 10);
        const minutes = parseInt(minuteStr, 10);

        if (period === 'PM' && hours !== 12) {
            hours += 12;
        }
        if (period === 'AM' && hours === 12) {
            hours = 0;
        }

        return hours * 60 + minutes;
    }

    function getMinutesSinceMidnight(timestamp) {
        const date = new Date(timestamp);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        return hours * 60 + minutes;
    }

    const closeModal = () => {
        setShowModal(false);
        setCancelling(false);
        setRescheduling(false);
        setSelectedSlot(null);
    }


    const handleChange = () => console.log("changed");

    const handleReschedule = async () => {
        if (!modalContent || !modalContent._id) {
            console.error("Invalid appointment data.");
            return;
        }

        let startDateTime, endDateTime, tech;

        if (selectedSlot) {
            startDateTime = selectedSlot.start;
            endDateTime = selectedSlot.end;
            tech = modalContent.appointmentDetails[5].artist;
        } else {
            const date = document.querySelector("input[name='date']")?.value;
            const startTime = document.querySelector("input[name='startTime']")?.value;
            const endTime = document.querySelector("input[name='endTime']")?.value;
            const nailTech = document.querySelector("select[name='nailTech']")?.value;

            if (!date || !startTime || !endTime || nailTech === "Select") {
                alert("Please complete all fields to reschedule.");
                return;
            }

            startDateTime = new Date(`${date}T${startTime}`);
            endDateTime = new Date(`${date}T${endTime}`);
            tech = nailTech;
        }

        const duration = Math.floor((endDateTime - startDateTime) / (1000 * 60));

        const updatedDetails = [...modalContent.appointmentDetails];
        updatedDetails[5] = {
            ...updatedDetails[5],
            date: startDateTime,
            duration: duration,
            artist: tech,
        };

        try {
            const res = await axios.put(`${API_BASE}/appointments/${modalContent._id}`, {
                ...modalContent,
                appointmentDetails: updatedDetails
            });

            if (res.status === 200) {
                const updated = filteredAppointments.map(appt =>
                    appt._id === modalContent._id ? res.data : appt
                );
                setFilteredAppointments(updated);
                setAppointments(updated);
            }

            closeModal();
            setSelectedSlot(null);
        } catch (err) {
            console.error("Failed to reschedule appointment:", err);
        }
    };



    return (
        <div className="ScheduleBackground">
            {showModal && (
                <div className="modal-backdrop">
                    {(!cancelling && !rescheduling) && <div className="modal SchedulePageModal">
                        <div className="DetailsHeader">
                            <h2 className="DetailsTitle">Appointment Details</h2>
                            <p className="DetailsDate">{formatDateTime(new Date(modalContent.appointmentDetails[5].date))}</p>
                        </div>
                        <div className="DetailsBox">
                            <div className="DetailsBoxColumn1">
                                <div>
                                    <p className="DetailKey">
                                        Name:&nbsp;
                                    </p>
                                    <p className="DetailValue">
                                        {modalContent.user?.name}
                                    </p>
                                </div>
                                {modalContent.instagram &&
                                    <div>
                                        <p className="DetailKey">
                                            Insta:&nbsp;
                                        </p>
                                        <p className="DetailValue">
                                            {modalContent.instagram}

                                        </p>
                                    </div>
                                }
                                <div>
                                    <p className="DetailKey">
                                        Email:&nbsp;
                                    </p>
                                    <p className="DetailValue">
                                        {modalContent.user?.email}

                                    </p>
                                </div>
                                {modalContent.answers &&
                                    <div>
                                        <p className="DetailKey">
                                            Est. Cost:&nbsp;
                                        </p>
                                        <p className="DetailValue">
                                            ${(
                                                modalContent.answers.reduce((runningTotal, x) => {
                                                    return runningTotal + ((x && x.price) ? x.price : 0);
                                                }, 0) - 10
                                            ).toFixed(2)}
                                        </p>
                                    </div>
                                }

                            </div>
                            <div className="DetailsBoxColumn2">
                                <div>
                                    <p className="DetailKey">
                                        Nail Service:&nbsp;
                                    </p>
                                    <p className="DetailValue">
                                        {modalContent.appointmentDetails[0]}
                                    </p>
                                </div>
                                {modalContent.appointmentDetails[2] &&
                                    <div>
                                        <p className="DetailKey">
                                            Design:&nbsp;
                                        </p>
                                        <p className="DetailValue">
                                            {modalContent.appointmentDetails[2]}
                                        </p>
                                    </div>
                                }
                                {modalContent.appointmentDetails[3] &&

                                    <div>
                                        <p className="DetailKey">
                                            Soak-Off:&nbsp;
                                        </p>
                                        <p className="DetailValue">
                                            {modalContent.appointmentDetails[3]}
                                        </p>
                                    </div>
                                }
                                {modalContent.appointmentDetails[1] &&
                                    <div>
                                        <p className="DetailKey">
                                            Length:&nbsp;
                                        </p>
                                        <p className="DetailValue">
                                            {modalContent.appointmentDetails[1]}
                                        </p>
                                    </div>
                                }
                            </div>
                        </div>

                        <div className="DetailsBoxActions">
                            <button onClick={closeModal} className="modal-close ScheduleClosingApparatus">
                                <img
                                    className="close-button-icon"
                                    src="/images/CloseButton.png"
                                    alt="Close Button Icon"
                                />
                            </button>
                            <button onClick={() => setCancelling(true)}>Cancel Appointment</button>
                            <button onClick={() => setRescheduling(true)}>Reschedule</button>
                        </div>
                    </div>}
                    {cancelling && <div className="modal">
                        <h2 className="DetailsTitle cancellationTitle">Appointment Cancellation</h2>
                        <div className="DetailsBox">
                            <p className="cancellationWarningText">Are you sure you want to cancel the client’s appointment? Canceled appointments can only be undone within the past 5 minutes and if slot is still available. </p>
                        </div>
                        <div className="DetailsExtra CancellationEverything">
                            <label className="cancellationReasonLabel">Reason for cancellation:</label>
                            <textarea name="cancellationReason" className="cancellationReason" placeholder="Enter reason here..."></textarea>
                        </div>

                        <div className="DetailsBoxActions">
                            <button onClick={closeModal} className="modal-close cancellingClose ScheduleClosingApparatus">
                                <img
                                    className="close-button-icon"
                                    src="/images/CloseButton.png"
                                    alt="Close Button Icon"
                                />
                            </button>
                            <button onClick={() => setCancelling(false)}>Go Back</button>
                            <button onClick={cancelAppointment}>Confirm</button>
                        </div>
                    </div>}
                    {rescheduling && <div className="modal reschedulingModal">
                        <h2 className="DetailsTitle reschedulingTitle">Reschedule Appointment</h2>
                        {/* <div className="DetailsBox">
                            <p>Are you sure you want to reschedule?</p>
                        </div> */}
                        <div className="DetailsExtra reschedulingExtra">
                            <p className="reschedulingWarningText">Are you sure you want to reschedule?</p>

                            {(() => {
                                const slots = getUpcomingAppointmentSlots(modalContent);

                                if (slots.length > 0) {
                                    const grouped = slots.reduce((acc, slot) => {
                                        const date = new Date(slot.start);
                                        const dateKey = date.toLocaleDateString("en-US", {
                                            weekday: "long",
                                            month: "long",
                                            day: "numeric"
                                        });
                                        if (!acc[dateKey]) acc[dateKey] = { date, slots: [] };
                                        acc[dateKey].slots.push(slot);
                                        return acc;
                                    }, {});

                                    // Now sort by the actual Date object
                                    const sortedGrouped = Object.entries(grouped)
                                        .sort((a, b) => a[1].date - b[1].date)
                                        .reduce((acc, [key, value]) => {
                                            acc[key] = value.slots;
                                            return acc;
                                        }, {});

                                    return (
                                        <>
                                            <label className="reschedulingTimesLabel">{modalContent.appointmentDetails[5].artist}'s Upcoming Availability:</label>
                                            <div className="AvailabilityListContainer">
                                                <div className="AvailabilityList">
                                                    {Object.entries(sortedGrouped).map(([date, slotGroup], i) => (
                                                        <div className="SlotSections">
                                                            <div key={i} className="SlotGroup">
                                                                <h4 className="SlotDate">{date}</h4>
                                                                {slotGroup.map((slot, j) => (
                                                                    <div
                                                                        key={j}
                                                                        className={`AvailableSlot ${selectedSlot?.start?.getTime() === slot.start.getTime() ? "SelectedSlot" : ""}`}
                                                                        onClick={() => setSelectedSlot(slot)}
                                                                    >
                                                                        {formatTime(slot.start)}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    );
                                } else {
                                    return (
                                        <div className="UnAvailable">
                                            <p className="highlight">Availability needs to be updated to reschedule client. Please go to Working Hours page and add your hours.</p>
                                            <h1>OR</h1>
                                            <p>Add client's preferred appointment date and time below.</p>
                                            <div className="OptionSet2 smallText">
                                                <label>
                                                    Appointment Date:
                                                    <input type="date" name="date" onChange={handleChange} />
                                                </label>

                                                <label>
                                                    Preferred Time:
                                                    <input type="time" name="startTime" onChange={handleChange} />
                                                </label>

                                                <label>
                                                    Est. End Time:
                                                    <input type="time" name="endTime" onChange={handleChange} />
                                                </label>

                                                <label>
                                                    Nail Tech:
                                                    <select name="nailTech" onChange={handleChange}>
                                                        <option>Select</option>
                                                        <option>Leah</option>
                                                        <option>Kayla</option>
                                                        <option>Kelly</option>
                                                    </select>
                                                </label>
                                            </div>
                                            <p className="highlight smallText">Appointment slot with {modalContent.appointmentDetails[5].artist} is not available. Please select another time.</p>
                                        </div>
                                    );
                                }
                            })()}

                        </div>

                        <div className="DetailsBoxActions reschedulingActions">
                            <button onClick={closeModal} className="modal-close reschedulingClose ScheduleClosingApparatus">
                                <img
                                    className="close-button-icon"
                                    src="/images/CloseButton.png"
                                    alt="Close Button Icon"
                                />
                            </button>
                            <button onClick={() => setRescheduling(false)}>Go Back</button>

                            <button onClick={handleReschedule}>Confirm</button>
                        </div>
                    </div>}
                    {/* <div className="modal">
                        <h2 className="DetailsTitle">Appointment Cancellation</h2>
                        <pre>{JSON.stringify(modalContent, null, 2)}</pre>
                        <button onClick={() => setShowModal(false)}>Close</button>
                        <div className="DetailsBox">
                            <p>Are you sure you want to reschedule?</p>
                        </div>
                        <div className="DetailsExtra">
                            <label>Upcoming Availability:</label>
                            <div className="AvailabilityList"></div>
                        </div>

                        <div className="DetailsBoxActions">
                            <button onClick={closeModal} className="modal-close">
                                <img
                                    className="close-button-icon"
                                    src="/images/CloseButton.png"
                                    alt="Close Button Icon"
                                />
                            </button>
                            <button>Go Back</button>
                            <button>Confirm</button>
                        </div>
                    </div> */}
                </div>
            )}
            <div className="Schedule">
                {/* <h2>Schedule</h2> */}

                <div className="WeekSelector">
                    {/* Week Selector */}
                    {/* <label>Select Week: </label> */}
                    <select
                        value={selectedWeek || ""}
                        onChange={(e) => setSelectedWeek(e.target.value)}
                    >
                        {weeks.map((weekStart, index) => {
                            const dateKey = formatToLocalDateKey(weekStart);
                            let label;

                            // if (index === 0) label = formatWeekRange(weekStart) + `  (Last Week)`;
                            // else if (index === 1) label = formatWeekRange(weekStart) + `  (This Week)`;
                            // else if (index === 2) label = formatWeekRange(weekStart) + `  (Next Week)`;
                            // else label = formatWeekRange(weekStart);

                            const isMobile = window.innerWidth < 666;
                            const heart = isMobile ? "🩷" : "🖤";

                            if (index === 1) label = heart + " " + formatWeekRange(weekStart);
                            else label = formatWeekRange(weekStart);

                            return (
                                <option className="WeekRangeSelectorOptions" key={dateKey} value={dateKey}>
                                    {label}
                                </option>
                            );
                        })}
                    </select>
                    {/* <h1>{formatDate(activeDate)}</h1> */}

                </div>

                {isMobile && (
                    <div className="MobileDates">
                        {weekDates.map((date, i) => {
                            return (
                                <div className={`MobileDayCell ${(formatDate(activeDate) === weekDates[i]) ? 'Highlight' : ''}`} style={{ minWidth: "175px" }} onClick={() => { setActiveDate(new Date(date)) }}>
                                    <h4 className="HeaderWeekDate">{String(new Date(date).getDate()).padStart(2, '0')}</h4>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="Table">
                    <div className="Days DesktopOnly" style={{ flex: "unset" }}>
                        {daysOfWeek.map((day, i) => {
                            return (
                                <div className="DayCell" style={{ minWidth: "175px", ... (day === '') && { flex: "unset", width: "120px", minWidth: "120px" } }}>
                                    <h4>{day}</h4>
                                </div>
                            );
                        })}
                    </div>

                    <div className="Days DesktopOnly" style={{ flex: "unset" }}>
                        <div className="DayCell" style={{ flex: "unset", width: "120px", minWidth: "120px" }}>
                            <h4> </h4>
                        </div>
                        {weekDates.map((date, i) => {
                            return (
                                <div className={`DayCell`} style={{ minWidth: "175px" }}>
                                    <h4 className={`HeaderWeekDate ${(formatDate(new Date()) === date) ? 'today' : ''}`}>{date}</h4>
                                </div>
                            );
                        })}
                    </div>

                    {/* Weekly Calendar */}
                    <div className="ScrollContainer">

                        <div className="Days">
                            {daysOfWeek.map((day, i) => {
                                // const hours = getItemsForDay(i, workingHours);
                                const dayAppointments = getItemsForDay(i, filteredAppointments);

                                return (
                                    <div className={`DayCell ${(formatDate(activeDate) === weekDates[i - 1]) ? 'Active' : ''} ${((i !== 0) && (formatDate(activeDate) !== weekDates[i - 1])) ? 'Hide' : ''} ${i === 0 ? 'Times' : ''}`} style={{ height: `${24 * 60 * 1.5}px`, ...(day === '') && { flex: "unset", width: "120px" } }}>

                                        <div className={`DayContent ${i === 0 ? 'Times' : ''}`} key={day} style={{ ...(day === '') && { backgroundColor: "#F1D2EB", width: "120px" } }}>
                                            <div>
                                                {day === '' &&
                                                    (generateTimeline().map((time) => {
                                                        return <p key={time} style={{ position: "absolute", margin: "0px", color: "black", top: `${timeStringToMinutes(time) * 1.5}px`, left: "0px", paddingLeft: "10px", width: "100%", borderTop: "1px solid #ffffff69" }}>{time}</p>
                                                    }))
                                                }
                                                {i !== 0 && dayAppointments.map((appt) => (
                                                    <div
                                                        key={appt._id}
                                                        className={`ScheduleEntry ${(appt.appointmentDetails[5].artist === "Leah") ? "leah" : (appt.appointmentDetails[5].artist === "Kayla") ? "kayla" : (appt.appointmentDetails[5].artist === "Kelly") ? "kelly" : ""}`}
                                                        style={{
                                                            position: "absolute",
                                                            height: `${(appt.appointmentDetails[5].duration * 1.5) - 11}px`,
                                                            marginLeft: (appt.appointmentDetails[5].artist === "Leah") && "calc(calc(calc(calc(100% - 270px) / 14) + 9px) * 2 / 3)" || (appt.appointmentDetails[5].artist === "Kayla") && "0px" || (appt.appointmentDetails[5].artist === "Kelly") && "calc(calc(calc(calc(100% - 270px) / 14) + 9px) * 4 / 3)",
                                                            top: `${(getMinutesSinceMidnight(appt.appointmentDetails[5].date) * 1.5) + 1}px`,
                                                            backgroundColor: (appt.appointmentDetails[5].artist === "Kayla") && "#9F78B8" || (appt.appointmentDetails[5].artist === "Kelly") && "#F7A823",
                                                            borderLeftColor: (appt.appointmentDetails[5].artist === "Kayla") && "#622E82" || (appt.appointmentDetails[5].artist === "Kelly") && "#B36A03",
                                                        }}
                                                        onClick={() => handleClick(appt)}
                                                    >
                                                        <strong className="ScheduleEntryName">{truncateWithEllipses(appt.user.name)}</strong><br />
                                                        <span className="ScheduleEntryStartTime">{formatTime(appt.appointmentDetails[5].date)} -</span><br />
                                                        <span className="ScheduleEntryEndTime">{formatTime((new Date(appt.appointmentDetails[5].date).getTime()) + (appt.appointmentDetails[5].duration * 60 * 1000))}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>
                </div>


            </div>
        </div >
    );
};

export default Schedule;
